import { useSelector } from "react-redux";
import { useAccount, useReadContracts } from "wagmi";
import useDappConfig from "./useDappConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { formatUnits, maxUint256 } from "viem";
import { ETH, yieldVaultContractConfig } from "@config/contractsConfig";
import { useEffect, useMemo } from "react";
import useDappChain from "./useDappChain";

const useYieldVaultAllowance = () => {
  const { selectedERC20YieldVault } = useSelector((state: any) => state.user);
  const { isTransactionProcessing } = useSelector((state: any) => state.stake);
  const { address: walletAddress } = useAccount();
  const { chain } = useDappChain();
  const { config } = useDappConfig();

  // const token = useMemo(() => {
  //   let token: any = yieldVaultConfig.tokens[selectedERC20YieldVault];
  //   return token;
  // }, [selectedERC20YieldVault]);

  const token = useMemo(() => {
    let token: any = config.tokens[selectedERC20YieldVault];

    if (selectedERC20YieldVault === config.xtoken.symbol) {
      token = {
        ...config.xtoken,
        tags: [],
      };
    }
    return token;
  }, [config, selectedERC20YieldVault]);

  const allowanceContracts = useMemo(() => {
    const contracts = [];
    const vaultAddress =
      yieldVaultContractConfig[chain.id]?.gainAdapterETH.address;

    contracts.push({
      ...token,
      functionName: "allowance",
      args: [walletAddress, vaultAddress],
    });

    return contracts;
  }, [chain.id, token, walletAddress]);

  // TODO: sholdn't be same as allowance
  const minDepositContracts = useMemo(() => {
    const contracts = [];
    const vaultConfig =
      yieldVaultContractConfig[chain.id]?.gainAdapterETH || {};

    contracts.push({
      ...vaultConfig,
      functionName: "minDepositAmount",
    });

    return contracts;
  }, [chain.id]);

  // TODO: shouldn't be same as allowance
  const vaultLimitContracts = useMemo(() => {
    const contracts = [];
    const vaultConfig =
      yieldVaultContractConfig[chain.id]?.gainAdapterETH || {};

    contracts.push({
      ...vaultConfig,
      functionName: "getEthReservedLimit",
      args: [yieldVaultContractConfig[chain.id]?.interactionPool.address],
    });

    return contracts;
  }, [chain.id]);

  const { data, refetch: refetchAllowance } = useReadContracts({
    contracts: [...allowanceContracts],
    query: {
      enabled: !!walletAddress,
      refetchInterval: 60_000,
    },
  });

  // TODO: sholdn't be same as allowance
  const { data: minDepositAmount } = useReadContracts({
    contracts: [...minDepositContracts],
    query: {
      enabled: !!walletAddress,
      refetchInterval: false,
    },
  });

  // TODO: sholdn't be same as allowance
  const { data: depositLimit } = useReadContracts({
    contracts: [...vaultLimitContracts],
    query: {
      enabled: !!walletAddress,
      refetchInterval: false,
    },
  });

  useEffect(() => {
    if (!isTransactionProcessing) {
      refetchAllowance();
    }
  }, [isTransactionProcessing]);

  const allowanceData = useMemo(() => {
    const allowance = {
      erc20Allowance: 0,
      minDepositAmount: 0,
      depositLimit: 0,
    };
    if (data && walletAddress) {
      allowance.erc20Allowance =
        selectedERC20YieldVault !== ETH
          ? +toFixedWithoutRounding(
              data[0]?.result
                ? formatUnits(data[0].result as bigint, token.decimals)
                : "0",
              8
            )
          : +toFixedWithoutRounding(
              formatUnits(maxUint256, config.nativeToken.decimals),
              8
            );
    }
    if (minDepositAmount) {
      allowance.minDepositAmount = +toFixedWithoutRounding(
        minDepositAmount[0]?.result
          ? formatUnits(minDepositAmount[0].result as bigint, 18)
          : "0",
        8
      );
    }

    if (depositLimit) {
      allowance.depositLimit = +toFixedWithoutRounding(
        depositLimit[0]?.result
          ? formatUnits(depositLimit[0].result as bigint, 18)
          : "0",
        8
      );
    }

    return allowance;
  }, [
    data,
    walletAddress,
    selectedERC20YieldVault,
    minDepositAmount,
    depositLimit,
  ]);

  return allowanceData;
};

export default useYieldVaultAllowance;
